import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  Printer,
  Download,
  Share2,
  MoreVertical,
  X,
  Edit,
} from 'lucide-react';

interface Paycheck {
  id: string;
  month: string;
  year: number;
  amount: number;
  status: 'paid' | 'pending' | 'processing';
  paidAt?: string;
}

interface TeacherFinanceTabProps {
  teacherId: string;
  teacherName: string;
}

const mockPaychecks: Paycheck[] = [
  { id: '1', month: 'January', year: 2024, amount: 2012, status: 'paid', paidAt: '2024-02-01' },
  { id: '2', month: 'February', year: 2024, amount: 2150, status: 'paid', paidAt: '2024-03-01' },
  { id: '3', month: 'March', year: 2024, amount: 1980, status: 'paid', paidAt: '2024-04-01' },
  { id: '4', month: 'April', year: 2024, amount: 2200, status: 'pending' },
  { id: '5', month: 'May', year: 2024, amount: 2100, status: 'processing' },
];

const yearlyChartData = [
  { month: 'Jan', amount: 2012 },
  { month: 'Feb', amount: 2150 },
  { month: 'Mar', amount: 1980 },
  { month: 'Apr', amount: 2200 },
  { month: 'May', amount: 2100 },
  { month: 'Jun', amount: 1900 },
  { month: 'Jul', amount: 1200 },
  { month: 'Aug', amount: 1400 },
  { month: 'Sep', amount: 2050 },
  { month: 'Oct', amount: 2180 },
  { month: 'Nov', amount: 2000 },
  { month: 'Dec', amount: 1800 },
];

const statusColors: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
};

export function TeacherFinanceTab({ teacherId, teacherName }: TeacherFinanceTabProps) {
  const [filterPeriod, setFilterPeriod] = useState('Monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  const [paymentInfo, setPaymentInfo] = useState({
    paymentType: 'Hourly',
    amount: '46',
    currency: 'Dollar',
    payGrade: 'Mid Level',
    accountHolderName: '',
    bankName: '',
    bankCode: '',
    accountNumber: '',
    ibanNumber: '',
    swiftBicNumber: '',
    country: '',
    transferCurrency: 'Dollar',
  });

  const maxAmount = Math.max(...yearlyChartData.map((d) => d.amount));
  const currentMonthIncome = 2012;
  const hourlyRate = 46;

  const handlePaymentInfoChange = (field: string, value: string) => {
    setPaymentInfo((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* Monthly Income Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Estimated Monthly Income</span>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <Edit className="w-4 h-4" />
            </button>
          </div>
          <p className="text-3xl font-bold text-gray-900">${currentMonthIncome.toLocaleString()}</p>
          <div className="flex items-center gap-2 mt-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-600">+12% from last month</span>
          </div>
        </div>

        {/* Pay Grade Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-500">Pay Grade</span>
            </div>
            <button
              onClick={() => setShowPaymentModal(true)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <Edit className="w-4 h-4" />
            </button>
          </div>
          <p className="text-3xl font-bold text-gray-900">${hourlyRate}<span className="text-lg font-normal text-gray-500">/hr</span></p>
          <p className="text-sm text-gray-500 mt-2">Mid Level Rate</p>
        </div>
      </div>

      {/* Yearly Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Yearly Income Overview</h2>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Printer className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Download className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="h-48">
          <div className="flex items-end justify-between h-full gap-2">
            {yearlyChartData.map((data) => (
              <div key={data.month} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-orange-500 rounded-t transition-all hover:bg-orange-600"
                  style={{ height: `${(data.amount / maxAmount) * 100}%` }}
                  title={`$${data.amount}`}
                />
                <span className="text-xs text-gray-500 mt-2">{data.month}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded" />
            <span className="text-sm text-gray-600">Monthly Income</span>
          </div>
        </div>
      </div>

      {/* Paychecks Table */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Paychecks</h2>
          <div className="flex items-center gap-3">
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Yearly">Yearly</option>
            </select>
            <div className="flex items-center gap-1">
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Printer className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Download className="w-4 h-4" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mockPaychecks.map((paycheck) => (
                <tr key={paycheck.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {paycheck.month} {paycheck.year}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    ${paycheck.amount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[paycheck.status]}`}>
                      {paycheck.status.charAt(0).toUpperCase() + paycheck.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {paycheck.paidAt || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setShowActionMenu(showActionMenu === paycheck.id ? null : paycheck.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {showActionMenu === paycheck.id && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                          <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Send Message
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Edit Payment Details
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Export as PDF
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Share
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50">
                            Send Payroll to User
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                            Cancel Payment
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Click outside to close action menu */}
      {showActionMenu && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowActionMenu(null)}
        />
      )}

      {/* Payment Information Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>
                <p className="text-sm text-gray-500">{teacherName}</p>
              </div>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
              {/* Payment Details Section */}
              <div>
                <h4 className="text-sm font-semibold text-orange-500 mb-4">Payment Details</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                    <select
                      value={paymentInfo.paymentType}
                      onChange={(e) => handlePaymentInfoChange('paymentType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="Hourly">Hourly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Global Salary">Global Salary</option>
                      <option value="One-Time Payment">One-Time Payment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      type="text"
                      value={paymentInfo.amount}
                      onChange={(e) => handlePaymentInfoChange('amount', e.target.value)}
                      placeholder="Type in amount"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <select
                      value={paymentInfo.currency}
                      onChange={(e) => handlePaymentInfoChange('currency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="Dollar">Dollar</option>
                      <option value="Euro">Euro</option>
                      <option value="Pound">Pound</option>
                      <option value="Shekel">Shekel</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pay Grade</label>
                  <select
                    value={paymentInfo.payGrade}
                    onChange={(e) => handlePaymentInfoChange('payGrade', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid Level">Mid Level</option>
                    <option value="Senior">Senior</option>
                  </select>
                </div>
              </div>

              {/* Transfer Details Section */}
              <div>
                <h4 className="text-sm font-semibold text-orange-500 mb-4">Transfer Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Details</label>
                    <div className="grid grid-cols-3 gap-4">
                      <input
                        type="text"
                        value={paymentInfo.accountHolderName}
                        onChange={(e) => handlePaymentInfoChange('accountHolderName', e.target.value)}
                        placeholder="Account Holder Name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="text"
                        value={paymentInfo.bankName}
                        onChange={(e) => handlePaymentInfoChange('bankName', e.target.value)}
                        placeholder="Bank Name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="text"
                        value={paymentInfo.bankCode}
                        onChange={(e) => handlePaymentInfoChange('bankCode', e.target.value)}
                        placeholder="Bank Code"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">More bank Details</label>
                    <div className="grid grid-cols-3 gap-4">
                      <input
                        type="text"
                        value={paymentInfo.accountNumber}
                        onChange={(e) => handlePaymentInfoChange('accountNumber', e.target.value)}
                        placeholder="Account Number"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="text"
                        value={paymentInfo.ibanNumber}
                        onChange={(e) => handlePaymentInfoChange('ibanNumber', e.target.value)}
                        placeholder="IBAN Number"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <input
                        type="text"
                        value={paymentInfo.swiftBicNumber}
                        onChange={(e) => handlePaymentInfoChange('swiftBicNumber', e.target.value)}
                        placeholder="SWIFT / BIC Number"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country & Currency</label>
                    <div className="grid grid-cols-2 gap-4">
                      <select
                        value={paymentInfo.country}
                        onChange={(e) => handlePaymentInfoChange('country', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">Country</option>
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                        <option value="IL">Israel</option>
                        <option value="DE">Germany</option>
                      </select>
                      <select
                        value={paymentInfo.transferCurrency}
                        onChange={(e) => handlePaymentInfoChange('transferCurrency', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="Dollar">Dollar</option>
                        <option value="Euro">Euro</option>
                        <option value="Pound">Pound</option>
                        <option value="Shekel">Shekel</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel Changes
              </button>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
